/** Format an amount given in cents as a localized currency string. */
export function formatCurrency(cents: number, currency = 'USD'): string {
  const code = (currency || 'USD').toUpperCase();
  const dollars = cents / 100;
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: code }).format(dollars);
  } catch {
    return `$${dollars.toFixed(2)}`;
  }
}

/** Parse epoch-ms number, numeric string, or ISO date string into a Date (or null). */
function parseDate(value: number | string): Date | null {
  if (value === null || value === undefined) return null;
  let date: Date;
  if (typeof value === 'number') date = new Date(value);
  else if (/^\d+$/.test(value.trim())) date = new Date(Number(value));
  else date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Format an epoch-ms / ISO date as e.g. "Jul 26, 2026". Em dash if unparseable. */
export function formatDate(value: number | string): string {
  const date = parseDate(value);
  if (!date) return '—';
  return new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'short', day: 'numeric' }).format(date);
}

/** Format an epoch-ms / ISO date as a relative "3 minutes ago" / "just now" string. Falls back to `formatDate` past a week, or on an unparseable value. */
export function timeAgo(value: number | string): string {
  const date = parseDate(value);
  if (!date) return '—';
  const seconds = Math.max(0, Math.round((Date.now() - date.getTime()) / 1000));
  if (seconds < 45) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
  return formatDate(value);
}
